import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import Placeholder from "@tiptap/extension-placeholder"
import { Markdown } from "tiptap-markdown"
import { getMetaValue } from "../plugins/src/metadata"

// Mounts a TipTap editor that replaces a hidden <textarea>. Reads the
// textarea's markdown on connect, parses it into the TipTap document,
// and writes serialized markdown back to the textarea on every update.
// The textarea is the canonical form field; TipTap is just the UI.
export default class extends Controller {
  static targets = ["editor", "input", "toolbarButton"]
  static values = {
    placeholder: { type: String, default: "Write here…" },
    imageUploadUrl: { type: String, default: "" }
  }

  connect() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          codeBlock: false,    // we keep inline code only; structural-only by design
          horizontalRule: false
        }),
        Link.configure({ openOnClick: false, autolink: true }),
        Image.configure({ inline: false, allowBase64: false }),
        Placeholder.configure({ placeholder: this.placeholderValue }),
        Markdown.configure({
          html: false,         // disallow raw HTML to keep the structural guardrail
          tightLists: true,
          linkify: false,
          breaks: false
        })
      ],
      content: this.inputTarget.value,
      editorProps: {
        attributes: {
          class: "tiptap prose prose-sm max-w-none min-h-[18rem] p-4 focus:outline-none dark:prose-invert"
        },
        handleDrop: (_view, event, _slice, moved) => this._handleImageDrop(event, moved),
        handlePaste: (_view, event) => this._handleImagePaste(event)
      },
      onUpdate: () => this._sync(),
      onSelectionUpdate: () => this._refreshButtonStates()
    })

    this._refreshButtonStates()
  }

  disconnect() {
    this.editor?.destroy()
  }

  // Toolbar actions — wired via data-action="click->tiptap-editor#toggleBold" etc.
  toggleBold(e)        { e.preventDefault(); this.editor.chain().focus().toggleBold().run() }
  toggleItalic(e)      { e.preventDefault(); this.editor.chain().focus().toggleItalic().run() }
  toggleCode(e)        { e.preventDefault(); this.editor.chain().focus().toggleCode().run() }
  toggleBulletList(e)  { e.preventDefault(); this.editor.chain().focus().toggleBulletList().run() }
  toggleOrderedList(e) { e.preventDefault(); this.editor.chain().focus().toggleOrderedList().run() }
  toggleBlockquote(e)  { e.preventDefault(); this.editor.chain().focus().toggleBlockquote().run() }

  setHeading(e) {
    e.preventDefault()
    const level = Number(e.currentTarget.dataset.level)
    this.editor.chain().focus().toggleHeading({ level }).run()
  }

  setParagraph(e) {
    e.preventDefault()
    this.editor.chain().focus().setParagraph().run()
  }

  setLink(e) {
    e.preventDefault()
    const previous = this.editor.getAttributes("link").href
    const url = window.prompt("Link URL", previous || "https://")
    if (url === null) return                  // cancelled
    if (url === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }
    this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
  }

  // Writes the current TipTap document back to the hidden textarea as
  // markdown. Called on every editor update — but NOT on connect, because
  // tiptap-markdown's parse → serialize round-trip can normalize
  // user-saved markdown (e.g. `*foo*` → `_foo_`) and produce a phantom
  // PaperTrail revision when an untouched slide is re-saved.
  _sync() {
    this.inputTarget.value = this.editor.storage.markdown.getMarkdown()
  }

  _refreshButtonStates() {
    if (!this.hasToolbarButtonTarget) return
    this.toolbarButtonTargets.forEach((btn) => {
      const mark = btn.dataset.markName
      const node = btn.dataset.nodeName
      const level = btn.dataset.level
      const attrs = level ? { level: Number(level) } : undefined
      let active = false
      if (mark) active = this.editor.isActive(mark)
      if (node) active = this.editor.isActive(node, attrs)
      btn.classList.toggle("bg-blue-100", active)
      btn.classList.toggle("dark:bg-blue-800", active)
      btn.classList.toggle("text-blue-700", active)
      btn.classList.toggle("dark:text-blue-100", active)
      btn.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  // Drop / paste handlers — intercept image files, upload to ActiveStorage,
  // then insert as an image node. tiptap-markdown serializes <img> nodes to
  // standard markdown `![alt](url)`. Returns true to mark the event handled.
  _handleImageDrop(event, moved) {
    if (moved) return false
    const file = this._firstImageFile(event.dataTransfer?.files)
    if (!file) return false
    event.preventDefault()
    this._uploadAndInsert(file)
    return true
  }

  _handleImagePaste(event) {
    const file = this._firstImageFile(event.clipboardData?.files)
    if (!file) return false
    event.preventDefault()
    this._uploadAndInsert(file)
    return true
  }

  _firstImageFile(files) {
    if (!files || !files.length) return null
    for (const f of files) if (f.type?.startsWith("image/")) return f
    return null
  }

  async _uploadAndInsert(file) {
    if (!this.imageUploadUrlValue) {
      console.warn("tiptap-editor: no imageUploadUrl set; cannot upload dropped image")
      return
    }
    try {
      const body = new FormData()
      body.append("upload", file)
      const response = await fetch(this.imageUploadUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": getMetaValue("csrf-token"), "Accept": "application/json" },
        body,
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`upload failed: ${response.status}`)
      const data = await response.json()
      if (!data.url) throw new Error("upload response missing url")
      this.editor.chain().focus().setImage({ src: data.url, alt: file.name }).run()
    } catch (err) {
      console.error("tiptap-editor image upload failed", err)
    }
  }
}
